/**
 * highlightSplit — cut a plain string into "plain" and "hit" pieces for find
 * highlighting. Walter 2026-08-24, for the agenda panel's rows.
 *
 * Pure and in its own file so the fiddly parts get tested: ranges arriving out
 * of order, overlapping each other, or pointing past the end of the text —
 * that last one happens for real, because a search can still be on screen when
 * the underlying reminder text changes.
 */

/**
 * splitHighlightRanges(text, ranges, current) →
 *   [{ text, hit: boolean, current: boolean }]
 *
 * `ranges` is [{from, to}] into `text`; `current` is the index into `ranges`
 * of the stepped-to hit (-1 or omitted for none). Pieces come back in order
 * and concatenate exactly back to `text`.
 */
export function splitHighlightRanges(text, ranges, current = -1) {
  const s = String(text ?? '');
  const list = Array.isArray(ranges) ? ranges : [];
  if (s.length === 0) return s === '' && list.length === 0 ? [] : [];

  const clamped = list
    .map((r, i) => ({
      from: Math.max(0, Math.min(Number(r?.from) || 0, s.length)),
      to: Math.max(0, Math.min(Number(r?.to) || 0, s.length)),
      i,
    }))
    .filter(r => r.to > r.from)
    .sort((a, b) => (a.from - b.from) || (a.to - b.to));

  const out = [];
  let at = 0;
  for (const r of clamped) {
    if (r.from < at) continue;                  // overlaps a hit already taken
    if (r.from > at) out.push({ text: s.slice(at, r.from), hit: false, current: false });
    out.push({ text: s.slice(r.from, r.to), hit: true, current: r.i === current });
    at = r.to;
  }
  if (at < s.length) out.push({ text: s.slice(at), hit: false, current: false });
  return out;
}
